import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    getPaginationRowModel,
    flexRender,
    type ColumnDef,
    type SortingState,
} from '@tanstack/react-table';
import { Button } from '@/kit';
import { platformApi } from '../../../api/endpoints/platform';
import { Gate } from '../../../components/permissions/Gate';
import { Badge } from '../../../components/ui/badge';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../../components/ui/table';
import { DataTableToolbar } from '../../../components/shared/DataTableToolbar';
import { DataTablePagination } from '../../../components/shared/DataTablePagination';
import { SortableHeader } from '../../../components/shared/SortableHeader';
import { ContentLoading } from '../../../components/shared/ContentLoading';
import { globalFilterFn } from '../../../lib/dataTable';
import { PlatformRoleForm } from './PlatformRoleForm';
import { PlatformRoleRowActions } from './PlatformRoleRowActions';
import type { PlatformRoleListItemLocal } from '../../../api/types/PlatformLocal';

// El rol de sistema platform por antonomasia (asignación seed). El backend marca `isSystem` de forma
// confiable en los roles platform (a diferencia del seed del tenant, que lo omite); el fallback por
// slug es defensa en profundidad por si una versión vieja del list lo trajera sin el flag.
const SYSTEM_ROLE_IDS = new Set(['platform_super_admin']);

function isSystemRole(role: PlatformRoleListItemLocal): boolean {
    return role.isSystem === true || SYSTEM_ROLE_IDS.has(role.roleId);
}

/**
 * Roles a nivel PLATAFORMA (`/platform/roles`). Misma composición que la tabla de roles del tenant
 * (useReactTable + DataTableToolbar/Pagination + SortableHeader + kebab por fila), pero la data sale
 * de `platformApi.*PlatformRole` y los gates usan los permisos `platform.role.*` (scope PLATFORM). El
 * scope del rol lo autocalcula el backend (DEC-037), así que el form no lo pide. Solo los roles custom
 * traen menú de acciones; los de sistema (isSystem) son inmutables.
 */
export function PlatformRolesTable() {
    const [createOpen, setCreateOpen] = useState(false);
    const [globalFilter, setGlobalFilter] = useState('');
    const [sorting, setSorting] = useState<SortingState>([{ id: 'displayName', desc: false }]);

    const { data, isLoading } = useQuery({
        queryKey: ['platform', 'roles'],
        queryFn: () => platformApi.listPlatformRoles(),
    });

    const columns = useMemo<ColumnDef<PlatformRoleListItemLocal>[]>(
        () => [
            {
                accessorKey: 'displayName',
                header: ({ column }) => <SortableHeader column={column} title="Rol" />,
            },
            {
                id: 'roleId',
                accessorFn: (row) => row.roleId,
                header: ({ column }) => <SortableHeader column={column} title="Slug" />,
                cell: (info) => (
                    <span className="text-muted-foreground">{info.getValue() as string}</span>
                ),
            },
            {
                id: 'scope',
                accessorFn: (row) => row.scope,
                header: ({ column }) => <SortableHeader column={column} title="Alcance" />,
                cell: (info) => <Badge variant="outline">{info.getValue() as string}</Badge>,
            },
            {
                id: 'tipo',
                accessorFn: (row) => (isSystemRole(row) ? 'Sistema' : 'Custom'),
                header: ({ column }) => <SortableHeader column={column} title="Tipo" />,
                cell: (info) => {
                    const system = isSystemRole(info.row.original);
                    return (
                        <Badge variant={system ? 'secondary' : 'default'}>
                            {system ? 'Sistema' : 'Custom'}
                        </Badge>
                    );
                },
            },
            {
                id: 'permissions',
                accessorFn: (row) => row.permissions?.length ?? 0,
                header: ({ column }) => <SortableHeader column={column} title="Permisos" />,
                enableGlobalFilter: false,
            },
            {
                id: 'actions',
                header: 'Acciones',
                enableSorting: false,
                enableGlobalFilter: false,
                cell: (info) =>
                    isSystemRole(info.row.original) ? null : (
                        <PlatformRoleRowActions role={info.row.original} />
                    ),
            },
        ],
        [],
    );

    const rows = useMemo(() => data?.items ?? [], [data]);
    const table = useReactTable({
        data: rows,
        columns,
        state: { globalFilter, sorting },
        onGlobalFilterChange: setGlobalFilter,
        onSortingChange: setSorting,
        globalFilterFn,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: { pagination: { pageSize: 25 } },
    });

    if (isLoading) return <ContentLoading />;

    return (
        <div className="space-y-3">
            <div className="flex justify-end">
                <Gate permission="platform.role.create" scope="PLATFORM">
                    <Button
                        onClick={() => {
                            setCreateOpen(true);
                        }}
                    >
                        Crear rol
                    </Button>
                </Gate>
            </div>

            {createOpen && (
                <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 pt-16">
                    <button
                        type="button"
                        aria-label="Cerrar"
                        className="fixed inset-0 bg-black/50"
                        onClick={() => {
                            setCreateOpen(false);
                        }}
                    />
                    <div className="relative w-full max-w-lg rounded-lg border bg-background p-6 shadow-lg">
                        <div className="mb-4 flex items-center justify-between">
                            <h2 className="text-lg font-semibold">Crear rol</h2>
                            <button
                                type="button"
                                aria-label="Cerrar"
                                className="text-muted-foreground hover:text-foreground"
                                onClick={() => {
                                    setCreateOpen(false);
                                }}
                            >
                                ✕
                            </button>
                        </div>
                        <PlatformRoleForm
                            onDone={() => {
                                setCreateOpen(false);
                            }}
                        />
                    </div>
                </div>
            )}

            <DataTableToolbar
                value={globalFilter}
                onChange={setGlobalFilter}
                placeholder="Buscar por nombre o slug…"
            />

            <Table>
                <TableHeader>
                    {table.getHeaderGroups().map((hg) => (
                        <TableRow key={hg.id}>
                            {hg.headers.map((h) => (
                                <TableHead key={h.id} className="text-center">
                                    {flexRender(h.column.columnDef.header, h.getContext())}
                                </TableHead>
                            ))}
                        </TableRow>
                    ))}
                </TableHeader>
                <TableBody>
                    {table.getRowModel().rows.map((r) => (
                        <TableRow key={r.id}>
                            {r.getVisibleCells().map((c) => (
                                <TableCell key={c.id} className="text-center align-middle">
                                    {flexRender(c.column.columnDef.cell, c.getContext())}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <DataTablePagination table={table} />
        </div>
    );
}
